import { Head, Link, router, useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
  status: number
}

interface GpsDevice {
  id: number
  name: string
}

interface User {
  id: number
  name: string
}

interface AssignmentData {
  id: number
  gps_device_id: number | null
  installed_by_id: number | null
  installed_at: string | null
  install_vehicle: string
  install_gps_location: string
  install_vehicle_location: string
  status: number
  removed: boolean
  removed_by_id?: number | null
  removed_at?: string | null
  remove_vehicle_location?: string
}

interface FormData {
  gps_device_id: number | null
  installed_by_id: number | null
  installed_at: string | null
  install_vehicle: string
  install_gps_location: string
  install_vehicle_location: string
  removed_by_id: number | null
  removed_at: string | null
  remove_vehicle_location: string
}

interface Props {
  case_record: CaseRecord
  assignment: AssignmentData
  gps_devices: GpsDevice[]
  users: User[]
  can_destroy?: boolean
  errors?: Record<string, string[]>
}

export default function Edit({
  case_record,
  assignment,
  gps_devices,
  users,
  can_destroy,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "GPS", href: `/cases/${case_record.id}/gps` },
    {
      title: "Edit",
      href: `/cases/${case_record.id}/gps/${assignment.id}/edit`,
    },
  ]

  // Server-derived: legacy-imported removals keep status 0, so removal state
  // comes from removed_at rather than the status code.
  const isRemoved = assignment.removed

  const { data, setData, patch, processing } = useForm<FormData>({
    gps_device_id: assignment.gps_device_id,
    installed_by_id: assignment.installed_by_id,
    installed_at: assignment.installed_at,
    install_vehicle: assignment.install_vehicle,
    install_gps_location: assignment.install_gps_location,
    install_vehicle_location: assignment.install_vehicle_location,
    removed_by_id: assignment.removed_by_id ?? null,
    removed_at: assignment.removed_at ?? null,
    remove_vehicle_location: assignment.remove_vehicle_location ?? "",
  })

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    patch(`/cases/${case_record.id}/gps/${assignment.id}`)
  }

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this GPS entry?")) {
      router.delete(`/cases/${case_record.id}/gps/${assignment.id}`)
    }
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit GPS - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Edit GPS Assignment</h1>

        <div className="rounded-lg bg-white p-6 shadow">
          <form onSubmit={submit} className="space-y-6">
            {/* Installation Details */}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Installation Details</h3>

                <div>
                  <label className={labelClass}>GPS Unit</label>
                  <select
                    className={inputClass}
                    value={data.gps_device_id ?? ""}
                    onChange={(e) =>
                      setData(
                        "gps_device_id",
                        e.target.value ? parseInt(e.target.value) : null,
                      )
                    }
                    required
                  >
                    <option value="">Select GPS unit...</option>
                    {gps_devices.map((device) => (
                      <option key={device.id} value={device.id}>
                        {device.name}
                      </option>
                    ))}
                  </select>
                  {errors?.gps_device_id && (
                    <p className={errorClass}>{errors.gps_device_id[0]}</p>
                  )}
                  {errors?.gps_device && (
                    <p className={errorClass}>{errors.gps_device[0]}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Install Date & Time</label>
                  <input
                    type="datetime-local"
                    className={inputClass}
                    value={data.installed_at ?? ""}
                    onChange={(e) => setData("installed_at", e.target.value)}
                    required
                  />
                  {errors?.installed_at && (
                    <p className={errorClass}>{errors.installed_at[0]}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Installed By</label>
                  <select
                    className={inputClass}
                    value={data.installed_by_id ?? ""}
                    onChange={(e) =>
                      setData(
                        "installed_by_id",
                        e.target.value ? parseInt(e.target.value) : null,
                      )
                    }
                    required
                  >
                    <option value="">Select employee...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                  {errors?.installed_by_id && (
                    <p className={errorClass}>{errors.installed_by_id[0]}</p>
                  )}
                  {errors?.installed_by && (
                    <p className={errorClass}>{errors.installed_by[0]}</p>
                  )}
                </div>
              </div>

              {/* Vehicle & Location Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">
                  Vehicle & Location Details
                </h3>

                <div>
                  <label className={labelClass}>Vehicle</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={data.install_vehicle}
                    onChange={(e) => setData("install_vehicle", e.target.value)}
                    placeholder="Tag+state, make, model, color"
                    required
                    maxLength={100}
                  />
                  <p className="text-hpi-med-gray mt-1 text-xs">
                    Describe the vehicle: tag+state, make, model, and color
                  </p>
                  {errors?.install_vehicle && (
                    <p className={errorClass}>{errors.install_vehicle[0]}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>
                    Placement of GPS on Vehicle
                  </label>
                  <textarea
                    className={inputClass}
                    rows={3}
                    value={data.install_gps_location}
                    onChange={(e) =>
                      setData("install_gps_location", e.target.value)
                    }
                    placeholder="Describe where the GPS device is placed on the vehicle"
                    required
                  />
                  {errors?.install_gps_location && (
                    <p className={errorClass}>
                      {errors.install_gps_location[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>
                    Location of Vehicle when GPS Installed
                  </label>
                  <textarea
                    className={inputClass}
                    rows={3}
                    value={data.install_vehicle_location}
                    onChange={(e) =>
                      setData("install_vehicle_location", e.target.value)
                    }
                    placeholder="Address or description of where the vehicle was located"
                    required
                  />
                  {errors?.install_vehicle_location && (
                    <p className={errorClass}>
                      {errors.install_vehicle_location[0]}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Removal Details (only shown if assignment has been removed) */}
            {isRemoved && (
              <>
                <hr className="border-hpi-light-gray" />
                <h3 className="text-lg font-medium">Removal Details</h3>
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label className={labelClass}>Removal Date & Time</label>
                    <input
                      type="datetime-local"
                      className={inputClass}
                      value={data.removed_at ?? ""}
                      onChange={(e) => setData("removed_at", e.target.value)}
                      required
                    />
                    {errors?.removed_at && (
                      <p className={errorClass}>{errors.removed_at[0]}</p>
                    )}
                  </div>

                  <div>
                    <label className={labelClass}>Removed By</label>
                    <select
                      className={inputClass}
                      value={data.removed_by_id ?? ""}
                      onChange={(e) =>
                        setData(
                          "removed_by_id",
                          e.target.value ? parseInt(e.target.value) : null,
                        )
                      }
                      required
                    >
                      <option value="">Select employee...</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                    {errors?.removed_by_id && (
                      <p className={errorClass}>{errors.removed_by_id[0]}</p>
                    )}
                    {errors?.removed_by && (
                      <p className={errorClass}>{errors.removed_by[0]}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>
                    Location of Vehicle when GPS Removed
                  </label>
                  <textarea
                    className={inputClass}
                    rows={3}
                    value={data.remove_vehicle_location}
                    onChange={(e) =>
                      setData("remove_vehicle_location", e.target.value)
                    }
                    placeholder="Address or description of where the vehicle was located when GPS was removed"
                    required
                  />
                  {errors?.remove_vehicle_location && (
                    <p className={errorClass}>
                      {errors.remove_vehicle_location[0]}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Actions */}
            <div className="flex justify-between">
              {can_destroy ? (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="bg-hpi-red hover:bg-hpi-red/80 rounded-md px-4 py-2 text-sm font-medium text-white"
                >
                  Delete
                </button>
              ) : (
                <div />
              )}
              <div className="flex space-x-3">
                <Link
                  href={`/cases/${case_record.id}/gps`}
                  className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={processing}
                  className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {processing ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
